import { Tooltip } from "@admin/components/atoms/Tooltip";
import { WeekPicker } from "@admin/components/molecules/Picker";
import { LocationUser, Shift } from "@enos-monorepo/lib";
import { getISOWeek, startOfWeek } from "date-fns";
import { Dispatch, SetStateAction, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { ClearWeekShiftsModal } from "../../Modals/ClearWeekShiftsModal";
import { NewOpenShiftWeeklyModal } from "../../Modals/NewOpenShiftWeeklyModal";
import { NewShiftWeeklyModal } from "../../Modals/NewShiftWeeklyModal";
import { CopyShiftsWeekModal } from "../Modals/CopyShiftsWeekModal/CopyShiftsWeekModal";

interface HeaderProps {
  selectedWeek: DateRange | undefined;
  setSelectedWeek: Dispatch<SetStateAction<DateRange | undefined>>;
  setAvalibilityVisible?: Dispatch<SetStateAction<boolean>>;
  personnels: LocationUser[] | undefined;
  shiftsLength: number;
  companyId: string;
  locationId: string;
  shifts?: Shift[];
}

export function Header({
  setSelectedWeek,
  setAvalibilityVisible,
  selectedWeek,
  personnels,
  shiftsLength,
  companyId,
  locationId,
  shifts,
}: HeaderProps) {
  const { t, i18n } = useTranslation();
  const [isNewShiftWeeklyModalOpen, setNewShiftWeeklyModalOpen] =
    useState(false);
  const [isNewOpenShiftWeeklyModalOpen, setNewOpenShiftWeeklyModalOpen] =
    useState(false);
  const [isCopyShiftsWeekModalOpen, setCopyShiftsWeekModalOpen] =
    useState(false);
  const [isClearWeekShiftsModalOpen, setClearWeekShiftsModalOpen] =
    useState(false);
  return (
    <div className="flex card-header rounded-t-xl flex-row justify-between px-6 py-[31px] bg-white">
      <div className="flex gap-10 items-center">
        <h3 className="card-title">
          {selectedWeek?.to && selectedWeek.from && (
            <span className="font-bold">
              {t("common.Week")} {getISOWeek(selectedWeek?.from ?? "")} /{" "}
              {new Intl.DateTimeFormat(i18n.language, {
                day: "2-digit",
                month: "short",
              }).format(selectedWeek?.from)}{" "}
              -{" "}
              {new Intl.DateTimeFormat(i18n.language, {
                day: "2-digit",
                month: "short",
              }).format(selectedWeek?.to)}
            </span>
          )}
        </h3>

        <div className="flex items-center">
          <label className="switch ">
            <input
              className=""
              name="check"
              type="checkbox"
              value={0}
              onChange={() => setAvalibilityVisible?.((prev) => !prev)}
            />
            <span className="switch-label">{t("shift.Availabilities")}</span>
          </label>
        </div>
      </div>

      <div className="flex flex-row gap-4">
        <Tooltip
          trigger={
            <i
              onClick={() => setNewShiftWeeklyModalOpen(true)}
              className="ki-duotone text-[#296573] ki-add-notepad text-2xl cursor-pointer"
            ></i>
          }
        >
          {t("shift.Plan New Shifts")}
        </Tooltip>
        <Tooltip
          trigger={
            <i
              onClick={() => setNewOpenShiftWeeklyModalOpen(true)}
              className="ki-duotone text-[#DFA000] ki-add-files text-2xl cursor-pointer"
            ></i>
          }
        >
          {t("shift.Plan Open Shifts")}
        </Tooltip>
        <Tooltip
          trigger={
            <i
              onClick={() => setCopyShiftsWeekModalOpen(true)}
              className="ki-duotone text-[#056EE9] ki-copy-success text-2xl cursor-pointer"
            ></i>
          }
        >
          {t("shift.Copy Week")}
        </Tooltip>
        <Tooltip
          trigger={
            <i
              onClick={() => setClearWeekShiftsModalOpen(true)}
              className="ki-duotone text-red-500 ki-trash-square text-2xl cursor-pointer"
            ></i>
          }
        >
          {t("shift.Clear Week")}
        </Tooltip>

        <WeekPicker
          setSelectedWeek={setSelectedWeek}
          disabledDate={{
            before: startOfWeek(new Date(), { weekStartsOn: 1 }),
          }}
        />
      </div>
      <NewShiftWeeklyModal
        selectedWeek={selectedWeek}
        personnels={
          personnels?.filter((person) => !person.isDeleted) as LocationUser[]
        }
        open={isNewShiftWeeklyModalOpen}
        onClose={() => setNewShiftWeeklyModalOpen(false)}
        companyId={companyId}
        companyLocationId={locationId}
      />
      <NewOpenShiftWeeklyModal
        selectedWeek={selectedWeek}
        open={isNewOpenShiftWeeklyModalOpen}
        onClose={() => setNewOpenShiftWeeklyModalOpen(false)}
        companyId={companyId}
        companyLocationId={locationId}
      />
      <CopyShiftsWeekModal
        open={isCopyShiftsWeekModalOpen}
        onClose={() => setCopyShiftsWeekModalOpen(false)}
        personnelList={
          personnels?.filter((person) => !person.isDeleted) as LocationUser[]
        }
        shiftsLength={shiftsLength}
        selectedWeek={selectedWeek as DateRange}
        companyId={companyId}
      />
      <ClearWeekShiftsModal
        open={isClearWeekShiftsModalOpen}
        onClose={() => setClearWeekShiftsModalOpen(false)}
        date={selectedWeek}
        shiftsWillBeDeleted={shifts?.filter(
          (shift) =>
            shift.plannedStartDate &&
            shift.plannedEndDate &&
            selectedWeek?.from &&
            selectedWeek?.to &&
            new Date(shift.plannedStartDate) >=
              new Date(
                new Date(new Date().setDate(new Date().getDate() + 1)).setHours(
                  0,
                  0,
                  0,
                  0,
                ),
              ) &&
            new Date(shift.plannedEndDate) <= selectedWeek?.to,
        )}
      />
    </div>
  );
}
